import { useEffect, useState } from "react";
import { LifeBuoy, MapPin, Clock, CheckCircle2, XCircle, Car, AlertTriangle, Phone } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  open: "bg-rose-500/20 border-rose-500/40 text-rose-300",
  replacement_dispatched: "bg-amber-500/20 border-amber-500/40 text-amber-300",
  declined: "bg-slate-500/20 border-slate-500/40 text-slate-300",
  closed: "bg-emerald-500/20 border-emerald-500/40 text-emerald-300",
};

export default function SosDashboard() {
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ issue_type: "guasto", issue_description: "", lat: "", lon: "", phone_fallback: "" });

  const load = async () => {
    try {
      const r = await api.get("/sos/tickets");
      setTickets(r.data?.items || []);
    } catch (e) { toast.error("Errore caricamento"); }
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const trigger = async () => {
    if (!form.issue_description.trim()) { toast.error("Descrivi il problema"); return; }
    try {
      const r = await api.post("/sos/trigger", {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : undefined,
        lon: form.lon ? parseFloat(form.lon) : undefined,
      });
      toast.success(r.data.message);
      setShowForm(false); setForm({ issue_type: "guasto", issue_description: "", lat: "", lon: "", phone_fallback: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const close = async (id) => {
    const res = window.prompt("Risoluzione?");
    if (!res) return;
    try { await api.post(`/sos/${id}/close?resolution=${encodeURIComponent(res)}`); toast.success("Ticket chiuso"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="sos-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            SOS Assistenza <LifeBuoy className="text-[#ff0033]" />
          </h1>
          <p className="text-sm text-white/45 mt-1">Tickets di emergenza · auto sostitutive · GPS tracking</p>
        </div>
        <button data-testid="sos-new" onClick={() => setShowForm(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
          <LifeBuoy size={14}/> Apri Ticket SOS
        </button>
      </div>

      {showForm && (
        <div className="bg-white/[0.02] border border-rose-500/40 rounded-2xl p-5 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <select value={form.issue_type} onChange={(e)=>setForm({...form, issue_type: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="incidente">🚗 Incidente</option>
              <option value="guasto">🔧 Guasto meccanico</option>
              <option value="foratura">🛞 Foratura gomma</option>
              <option value="batteria">🔋 Batteria scarica</option>
              <option value="furto">🚨 Furto/Effrazione</option>
              <option value="altro">❓ Altro</option>
            </select>
            <input data-testid="sos-phone" placeholder="📞 Telefono cliente (fallback)" value={form.phone_fallback} onChange={(e)=>setForm({...form, phone_fallback: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="sos-lat" placeholder="📍 GPS Latitudine (es. 45.4642)" value={form.lat} onChange={(e)=>setForm({...form, lat: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="sos-lon" placeholder="📍 GPS Longitudine (es. 9.19)" value={form.lon} onChange={(e)=>setForm({...form, lon: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </div>
          <textarea data-testid="sos-desc" placeholder="Descrizione problema..." value={form.issue_description} onChange={(e)=>setForm({...form, issue_description: e.target.value})} rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button data-testid="sos-submit" onClick={trigger} className="btn-neon px-5 py-2 rounded-full text-xs">Apri Ticket</button>
            <button onClick={() => setShowForm(false)} className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-xs">Annulla</button>
          </div>
        </div>
      )}

      <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Aperto</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Descrizione</th>
              <th className="text-left p-3">GPS</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-white/40 text-xs">Nessun ticket SOS</td></tr>}
            {tickets.map((t) => (
              <tr key={t.id} className="border-t border-white/5" data-testid={`sos-row-${t.id}`}>
                <td className="p-3 text-[11px] text-white/60">{t.created_at?.slice(0,19).replace("T"," ")}</td>
                <td className="p-3 text-xs">{t.issue_type}</td>
                <td className="p-3 text-xs max-w-xs truncate">{t.issue_description}</td>
                <td className="p-3 text-xs font-mono">
                  {t.gps_lat ? (
                    <a href={`https://maps.google.com/?q=${t.gps_lat},${t.gps_lon}`} target="_blank" rel="noopener noreferrer" className="text-blue-300 inline-flex items-center gap-1"><MapPin size={11}/> {t.gps_lat.toFixed(4)},{t.gps_lon.toFixed(4)}</a>
                  ) : <span className="text-white/30">—</span>}
                </td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${STATUS_COLORS[t.status] || ""}`}>{t.status}</span></td>
                <td className="p-3 text-right">
                  {t.status !== "closed" && (
                    <button data-testid={`sos-close-${t.id}`} onClick={() => close(t.id)} className="px-2 py-1 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px]">Chiudi</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
